/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import * as Styled from "../../styles";
import Button from "@components/common/Button";
import type {
  IAccountsComponents,
  ValueFormikAccountLegal,
} from "src/@types/Sales";
import { useFormik } from "formik";
import { isEmail } from "@helpers/emailCheck";
import salesService from "@services/Sales/SalesService";
import Spinner from "@components/common/Spinner";
import enterprisesServiceInstance from "@services/Enterprises/enterprisesService";
import Alert from "@components/common/Alert";
import { LoginContext } from "@context/loginContext";
import { UsuallyContext } from "@context/usuallyContex";
import { SectionMainInformations } from "../../components/SectionLegal/SectionMainInformationsComponent";
import { SectionProductsInterest } from "../../components/SectionProductsInterestComponent";
import { cnpj } from "cpf-cnpj-validator";
import { phoneMask } from "@helpers/format";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { useOnlyMessageToast } from "@hook/useMessageToast";
import { OportunityContext } from "@context/oportunityContex";
import { useLocation, useNavigate } from "react-router-dom";

const AccountsLegalComponents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillData = location.state?.prefill as
    | { value: string; type: string }
    | undefined;

  // useCallback: referência estável evita re-render desnecessário do filho
  const handleBack = useCallback(() => {
    navigate("/oportunidades");
  }, [navigate]);

  return (
    <AccountsLegalComponentsOriginal
      onBack={handleBack}
      prefillData={prefillData}
      setMandatoryCreateAccount={() => {}}
      setCnpjCreateAccount={() => {}}
      setCpfCreateAccount={() => {}}
    />
  );
};

export const AccountsLegalComponentsOriginal = React.memo(
  ({
    onBack,
    isCreateAccount,
    setMandatoryCreateAccount,
    setCnpjCreateAccount,
    setCpfCreateAccount,
    prefillData,
  }: IAccountsComponents) => {
    // useRef para localStorage: valores estáveis durante o ciclo de vida do componente, sem disparar re-renders
    const idCorretor = useRef<string>(
      localStorage.getItem("@idCorretor") ?? "",
    );
    const regionalUser = useRef<string>(
      localStorage.getItem("@regional") ?? "",
    );
    const regional = regionalUser.current;

    const displayMessage = useOnlyMessageToast();
    const displayMessageError = useMessageErrorToast();
    const { signOut } = React.useContext(LoginContext);
    const { setIndexTabDetails } = React.useContext(OportunityContext);
    const {
      setSuccessCreateAccountSales,
      setNameCreateAccountSales,
      setAccountIdSalesforce,
      setTypeAccount,
    } = React.useContext(UsuallyContext);

    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [commercialRegional, setCommercialRegional] = useState("");
    const [businessUnity, setBusinessUnity] = useState("");
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [purposePurchase, setPurposePurchase] = useState("");
    const [detailsLive, setDetailsLive] = useState("");
    const [numberRooms, setNumberRooms] = useState("");
    const [numberVacancy, setNumberVacancy] = useState("");
    const [typeWork, setTypeWork] = useState("");
    const [require, setRequire] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingEnterprises, setLoadingEnterprises] = useState(false);
    const [dataEnterprises, setDataEnterprises] = useState([]);
    const [idEnterprises, setIdEnterprises] = useState("");
    const [error, setError] = useState({
      email: false,
      telefone: false,
      cnpj: false,
    });
    const [mainEnterprises, setMainEnterprises] = useState("");
    const [selectMainEnterprises, setSelectMainEnterprises] = useState("");
    const [originAccount, setOriginAccount] = useState("");

    // useMemo: recalcula apenas quando selectedValues muda, evitando múltiplos .includes() em cada render
    const { isWhatsAppPrincipal, isCelularPrincipal } = useMemo(
      () => ({
        isWhatsAppPrincipal: selectedValues.includes("Whatsapp"),
        isCelularPrincipal: selectedValues.includes("Celular"),
      }),
      [selectedValues],
    );

    // useMemo: objeto criado apenas uma vez, sem recriar a cada render
    const initialValues = useMemo(
      () => ({
        nameAccount: "",
        fantasyName: "",
        stateRegistration: "",
        munipalRegistration: "",
        cnpj:
          prefillData?.type === "CPFCNPJ" && prefillData?.value?.length === 18
            ? prefillData.value
            : "",
        nire: "",
        mainPhone:
          prefillData?.type === "Telefone" ? phoneMask(prefillData.value) : "",
        mainEmail: prefillData?.type === "Email" ? prefillData.value : "",
        dateFoundation: "",
        complementaryPhone: "",
        complementaryEmail: "",
        comments: "",
      }),
      [prefillData],
    );

    const handleSubmitFormik = useCallback(
      async (value: ValueFormikAccountLegal) => {
        setLoading(true);
        setMandatoryCreateAccount(false);
        setCnpjCreateAccount(false);

        const bodyIsCreateAccount = {
          idCorretorSalesforce: idCorretor.current,
          tipoRegistro: "Cliente - Pessoa Jurídica",
          nomeDaConta: value?.nameAccount,
          nomeFantasia: value?.fantasyName,
          regional,
          inscricaoEstadual: value?.stateRegistration,
          inscricaoMunicipal: value?.munipalRegistration,
          cnpj: cnpj.format(value?.cnpj),
          nire: value?.nire,
          regionalComercial: commercialRegional,
          telefonePrincipal: phoneMask(value?.mainPhone),
          emailPrincipal: value?.mainEmail,
          dataDeFundacao: value?.dateFoundation,
          unidadeDeNegocio: businessUnity,
          telefonePrincipalEhWhatsapp: isWhatsAppPrincipal,
          telefonePrincipalEhCelular: isCelularPrincipal,
          criarOportunidade: isCreateAccount ? false : true,
        };

        const body = {
          idCorretorSalesforce: idCorretor.current,
          tipoRegistro: "Cliente - Pessoa Jurídica",
          origemDaConta: originAccount,
          nomeDaConta: value?.nameAccount,
          nomeFantasia: value?.fantasyName,
          regional,
          inscricaoEstadual: value?.stateRegistration,
          inscricaoMunicipal: value?.munipalRegistration,
          cnpj: cnpj.format(value?.cnpj),
          nire: value?.nire,
          regionalComercial: commercialRegional,
          telefonePrincipal: phoneMask(value?.mainPhone),
          emailPrincipal: value?.mainEmail,
          dataDeFundacao: value?.dateFoundation,
          unidadeDeNegocio: businessUnity,
          emailComplementar: value?.complementaryEmail,
          finalidadeDaCompra: purposePurchase,
          detalhesPodeMorar: detailsLive,
          tipoDeTrabalho: typeWork,
          observacoes: value.comments,
          telefonePrincipalEhWhatsapp: isWhatsAppPrincipal,
          telefonePrincipalEhCelular: isCelularPrincipal,
          empreendimentoDeInteressePrincipal: idEnterprises,
          numeroDeQuartos: numberRooms,
          numeroDeVagas: numberVacancy,
          criarOportunidade: isCreateAccount ? false : true,
        };

        try {
          const response = await salesService.createPersonLegal(
            isCreateAccount ? bodyIsCreateAccount : body,
          );

          if (
            response?.status === 400 &&
            (response?.data?.emailJaUtilizado ||
              response?.data?.telefoneJaUtilizado ||
              response?.data?.cpfCnpjJaUtilizado)
          ) {
            setError((prevState) => ({
              ...prevState,
              email: response?.data.emailJaUtilizado,
              telefone: response?.data.telefoneJaUtilizado,
              cnpj: response?.data.cpfCnpjJaUtilizado,
            }));
            setLoading(false);
            const messageErro = response?.data?.erro;
            displayMessageError({
              message: messageErro ?? "Erro ao criar conta!",
            });
            return;
          }

          if (response?.status === 400) {
            const messageErro = response?.data;
            setLoading(false);
            displayMessageError({
              message: messageErro ?? "Erro ao criar conta!",
            });
            return;
          }

          if (response?.status === 401) {
            setLoading(false);
            setIsUnauthorized(true);
            return;
          }

          if (isCreateAccount) {
            onBack();
            displayMessage("Conta criada com sucesso!");
            setMandatoryCreateAccount(true);
            setCnpjCreateAccount(true);
            setCpfCreateAccount(cnpj.format(value?.cnpj));
          } else {
            setSuccessCreateAccountSales(true);
            setNameCreateAccountSales(value?.nameAccount);
            setAccountIdSalesforce(response?.data.id);
          }

          setIndexTabDetails(0);
          setTypeAccount("");
          setLoading(false);
        } catch (error) {
          console.log("ErrorFormik", error);
          setLoading(false);
        }
      },
      [
        regional,
        commercialRegional,
        businessUnity,
        isWhatsAppPrincipal,
        isCelularPrincipal,
        originAccount,
        purposePurchase,
        detailsLive,
        typeWork,
        idEnterprises,
        numberRooms,
        numberVacancy,
        isCreateAccount,
        onBack,
        displayMessage,
        displayMessageError,
        setMandatoryCreateAccount,
        setCnpjCreateAccount,
        setSuccessCreateAccountSales,
        setNameCreateAccountSales,
        setAccountIdSalesforce,
        setIndexTabDetails,
        setTypeAccount,
      ],
    );

    const formik: any = useFormik({
      initialValues,
      enableReinitialize: true,
      onSubmit: handleSubmitFormik,
    });

    const handleGetEnterprises = useCallback(async () => {
      setLoadingEnterprises(true);
      try {
        const response = await enterprisesServiceInstance.getEnterprises({
          regional: regionalUser.current,
        });

        if (response.status >= 400 && response?.status !== 401) {
          setLoadingEnterprises(false);
          setDataEnterprises([]);
          console.log("status 400", response);
          return;
        }
        if (response.status === 401) {
          setLoadingEnterprises(false);
          setIsUnauthorized(true);
          setDataEnterprises([]);
          return;
        }

        const dataArray: any = Object.values(response);
        setDataEnterprises(dataArray);
        setLoadingEnterprises(false);
      } catch (error) {
        setLoadingEnterprises(false);
        console.log("errorHandleGetEnterprises", error);
      }
    }, []);

    // useMemo: recalcula a validação apenas quando os campos relevantes mudam, evitando o custo do useEffect + setState em todo render do formik
    const isRequirementMet = useMemo(() => {
      const nameAccount = formik.getFieldProps("nameAccount").value;
      const cnpjValue = formik.getFieldProps("cnpj").value;
      const mainPhone = formik.getFieldProps("mainPhone").value;
      const mainEmail = formik.getFieldProps("mainEmail").value;
      const complementaryEmail =
        formik.getFieldProps("complementaryEmail").value;

      const nameInvalid =
        !nameAccount ||
        /\d/.test(nameAccount) ||
        /[^a-zA-ZÀ-ÖØ-öø-ÿ\s]/.test(nameAccount);

      const cnpjInvalid =
        cnpj.format(cnpjValue)?.length < 14 || !cnpj.isValid(cnpjValue);

      const phoneInvalid =
        !phoneMask(mainPhone) || phoneMask(mainPhone)?.length < 15;

      const emailInvalid = !mainEmail || (!isEmail(mainEmail) && mainEmail);

      const complementaryEmailInvalid =
        !isEmail(complementaryEmail) && complementaryEmail;

      const baseRequirements =
        nameInvalid ||
        !businessUnity ||
        cnpjInvalid ||
        phoneInvalid ||
        emailInvalid ||
        complementaryEmailInvalid;

      if (isCreateAccount) {
        return baseRequirements;
      }

      return baseRequirements || !originAccount;
    }, [
      formik.values.nameAccount,
      formik.values.cnpj,
      formik.values.mainPhone,
      formik.values.mainEmail,
      formik.values.complementaryEmail,
      businessUnity,
      originAccount,
      isCreateAccount,
    ]);

    // useEffect agora apenas sincroniza o resultado já calculado pelo useMemo, sem recalcular lógica pesada dentro dele
    useEffect(() => {
      setRequire(isRequirementMet);
    }, [isRequirementMet]);

    return (
      <>
        <Alert
          onOpen={isUnauthorized}
          title="Sessão expirada"
          titleButton={"Fazer login novamente"}
          description={
            "Sua sessão expirou, faça login novamente para ter acesso."
          }
          onClick={() => {
            signOut();
          }}
        />

        {!isCreateAccount && (
          <Styled.ContainerTitlePage>
            <Styled.ContainerTitle>
              <Styled.TitlePage>Contas</Styled.TitlePage>
            </Styled.ContainerTitle>
          </Styled.ContainerTitlePage>
        )}

        <Styled.ContainerBoxAccounts isRelationship={isCreateAccount}>
          {!isCreateAccount && (
            <Styled.ContainerTitleBox>
              <Styled.ContainerTitleWithCloseButton>
                <span>Nova Conta: Cliente - Pessoa Jurídica</span>
              </Styled.ContainerTitleWithCloseButton>
              <Styled.IconAiOutlineCloseCircle onClick={onBack} />
            </Styled.ContainerTitleBox>
          )}

          <Styled.ContainerBoxInfo>
            <SectionMainInformations
              formik={formik}
              error={error}
              setError={setError}
              regional={regional}
              originAccount={originAccount}
              setOriginAccount={setOriginAccount}
              commercialRegional={commercialRegional}
              setCommercialRegional={setCommercialRegional}
              selectedCheckBox={selectedValues}
              setSelectedCheckBox={setSelectedValues}
              businessUnity={businessUnity}
              setBusinessUnity={setBusinessUnity}
              purposePurchase={purposePurchase}
              setPurposePurchase={setPurposePurchase}
              detailsLive={detailsLive}
              setDetailsLive={setDetailsLive}
              typeWork={typeWork}
              setTypeWork={setTypeWork}
              isCreateAccount={isCreateAccount}
            />
          </Styled.ContainerBoxInfo>

          {!isCreateAccount && (
            <Styled.ContainerBoxInfo>
              <SectionProductsInterest
                setIdEnterprise={setIdEnterprises}
                selectMainEnterprises={selectMainEnterprises}
                setSelectMainEnterprises={setSelectMainEnterprises}
                dataEnterprises={dataEnterprises}
                mainEnterprises={mainEnterprises}
                setMainEnterprises={setMainEnterprises}
                loadingEnterprises={loadingEnterprises}
                getEnterprises={handleGetEnterprises}
                numberRooms={numberRooms}
                setNumberRooms={setNumberRooms}
                numberVacancy={numberVacancy}
                setNumberVacancy={setNumberVacancy}
              />
            </Styled.ContainerBoxInfo>
          )}

          <Styled.DividerBoxInput />
          <Styled.ContainerFooterBox>
            <Styled.ContainerMandatory>
              <span>(*) Campo de preenchimento obrigatório</span>
            </Styled.ContainerMandatory>
            <Styled.ContainerButton>
              <Button error title="CANCELAR" onClick={onBack} />
              <Button
                title={loading ? <Spinner /> : "CRIAR CONTA"}
                onClick={formik.handleSubmit}
                disabled={
                  require ||
                  loading ||
                  error.email ||
                  error.telefone ||
                  error.cnpj
                }
              />
            </Styled.ContainerButton>
          </Styled.ContainerFooterBox>
        </Styled.ContainerBoxAccounts>
      </>
    );
  },
);

export default AccountsLegalComponents;
